import React from "react"
import { makeStyles } from "tss-react/mui"
import { Box, Card, Chip, IconButton, Stack, Typography } from "@mui/material"
import { useExternalLinks } from "../../../../../../../shared/hooks/useAppLinks"
import { KitchenNews } from "../../../weekMenuTypes"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowUpRightFromSquare, faArrowUpRight } from "@fortawesome/pro-light-svg-icons"
import { tailwindPalette } from "../../../../../../theme/tailwindPalette"
import { LocalizedEnum } from "../../../../../../localization/components/AppLocalized"
import { faNewspaper } from "@fortawesome/pro-solid-svg-icons"
import { CardImage } from "../../CardImage"

const useStyles = makeStyles()(() => ({
    card: {
        boxShadow: "none",
        cursor: "pointer",
    },
    cardContent: {
        padding: ".375rem 0 0 0",
    },
    topContent: {
        position: "relative",
    },
    chip: {
        display: "flex",
        borderRadius: ".25rem",
        backgroundColor: "#5cb85c",
        borderColor: "#5cb85c",
        color: "white",
        fontWeight: 500,
    },
    chipWrap: {
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 2,
    },
    subtitle: {
        fontSize: ".75rem",
        color: tailwindPalette.slate[500],
    },
    title: {
        lineHeight: "1.05rem",
    },
}))

type NewsCardProps = {
    newsItem: KitchenNews
    onClick: (news: KitchenNews) => void
}

export function NewsCard({ newsItem, onClick }: NewsCardProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const openExternalLink = useExternalLinks()

    const onCardClick = () => {
        if (newsItem.external && newsItem.permaLink) {
            openExternalLink(newsItem.permaLink)
        } else {
            onClick(newsItem)
        }
    }

    return (
        <Card onClick={onCardClick} className={classes.card}>
            <Box className={classes.topContent}>
                <CardImage imageUri={newsItem.imageUrl} alt={newsItem.subject} icon={faNewspaper} />

                {newsItem.category && (
                    <Box className={classes.chipWrap}>
                        <LocalizedEnum
                            base="user-home-canteen-news-category"
                            enumValue={newsItem.category}
                            attrs={{ label: true }}
                        >
                            <Chip label={newsItem.category} size="small" className={classes.chip} />
                        </LocalizedEnum>
                    </Box>
                )}
            </Box>
            <Box className={classes.cardContent}>
                <Stack direction="row" alignItems="start" justifyContent="space-between">
                    <Typography variant="subtitle2" className={classes.title} pt=".2rem" mb=".5rem">
                        {newsItem.subject}
                    </Typography>
                    <IconButton onClick={onCardClick} size="small">
                        <FontAwesomeIcon
                            size="xs"
                            icon={newsItem.external ? faArrowUpRightFromSquare : faArrowUpRight}
                        />
                    </IconButton>
                </Stack>

                <Typography variant="body2" className={classes.subtitle}>
                    <Stack direction="row" alignItems="center" spacing=".5rem">
                        {dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(newsItem.created))}
                    </Stack>
                </Typography>
            </Box>
        </Card>
    )
}
